import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
} from "react-native";
import React, { useEffect, useState, useRef, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";

import GlobalStyles from "../../Resources/Styles";
import Colors from "../../Resources/Color";

import { Observer, observer } from "mobx-react";
import { useScreenDimensions, useDevice } from "./../_layout";
import { useTranslation } from "react-i18next";

import { AntDesign, FontAwesome6, Ionicons } from "@expo/vector-icons";

import { Menu, MenuItem } from "react-native-material-menu";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Feather } from "@expo/vector-icons";
import { reaction, runInAction } from "mobx";
import Serie from "../Firestore/Serie";
import CustomAlert from "../Components/CustomAlert";
import { ProtocolTitles } from "../../Resources/Constants";
import { formatNumber1Dec, formatNumber2Dec } from "../utils/numberUtils";
import {
  getProtocolSuggestion,
  hasProtocolTextError,
  updateExerciseWithProtocol,
  hasSeriesWithActualData,
  handleProtocolKeyPress,
} from "../utils/protocolTextUtils";
import { ScrollView } from "react-native-web";
import MediaManager from "../Classes/MediaManager";

import PulseSkeleton from "../Components/PulseSkeleton";
import FloatingExerciseLink from "../Components/FloatingExerciseLink";
import Tooltip from "../Components/Tooltip";
import TypingAnimation from "../Components/TypingAnimation";
import SlotTextNew from "./SlotTextNew";

const ProtocolRowTitleToggle = observer(
  ({ block, protocolTitle, calendar }) => {
    const { t } = useTranslation();
    const { isMobile } = useDevice();
    const [isHovered, setIsHovered] = useState(false);

    return (
      <TouchableOpacity
        style={{
          flex: 1,
          alignItems: "center",
          justifyContent: "flex-start",
          paddingHorizontal: 8,
          paddingVertical: 2,
          flexDirection: "row",
          outlineStyle: "none",
        }}
        onPress={() => {
          const prevState = block.getProtocolSeriesShown(protocolTitle);
          block.toggleProtocolSeriesShown(protocolTitle);

          if (calendar && block.client) {
            const macroBlocks = block.client.blocks.filter(
              (b) => b.label === block.label && b.id != block.id,
            );
            macroBlocks.forEach((b) => {
              if (b.getProtocolSeriesShown(protocolTitle) === prevState) {
                b.toggleProtocolSeriesShown(protocolTitle);
              }
            });
          }
        }}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Feather
          name={
            block.getProtocolSeriesShown(protocolTitle)
              ? "chevron-up"
              : "chevron-down"
          }
          size={16}
          color={Colors.bubbleColor}
          style={{ marginRight: 4 }}
        />
        <Text
          style={{
            textAlign: "left",
            fontSize: 12,
            fontWeight: "500",
            color: isHovered ? Colors.darkgrayColor : Colors.grayColor,
            flex: 1,
            transition: "color 200ms ease",
          }}
          numberOfLines={2}
        >
          {t(protocolTitle)}
        </Text>
      </TouchableOpacity>
    );
  },
);

const ProtocolRow = ({
  block,
  protocolTitle,
  calendar = false,
  visibleRange,
  columnWidth,
  showSetPriority = true,
}) => {
  const { t, i18n } = useTranslation();
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();

  // Pending focus requests for protocol text inputs after exercise selection.
  // Map<key, { wasEditing: boolean }> – keyed by `${protocolTitle}:${week}:${isSecondary}`.
  // Lives at ProtocolRow level so it survives SlotOfWeek remounts.
  const protocolTextFocusRequestsRef = useRef(new Map());

  // Tracks current edit-mode state so we can snapshot it when a focus request is made.
  const protocolTextEditModesRef = useRef(new Set());

  // Bump to trigger focus effects (refs alone don't cause re-renders).
  const [focusRequestVersion, setFocusRequestVersion] = useState(0);

  const getSlotColor = (protocolSlot) => {
    // 1. Strip the "-string" suffix
    const raw = protocolSlot.replace(/-string$/, "");
    // 2. Find the first uppercase letter to split priority vs. type
    const idx = raw.search(/[A-Z]/);
    const priority = idx !== -1 ? raw.substring(0, idx).toLowerCase() : "";

    // 3. Return the color based on priority
    switch (priority) {
      case "primary":
        return "rgba(255, 0, 0, 1)"; // Red
      case "secondary":
        return "rgba(63, 123, 141, 1)"; // Blue‐gray
      case "tertiary":
        return "rgba(34, 139, 34, 1)"; // Green
      case "quaternary":
        return "rgba(255, 165, 0, 1)"; // Orange
      case "quinary":
        return "rgba(255, 192, 203, 1)"; // Pink
      default:
        return "rgba(0, 0, 0, 0.1)"; // Fallback light gray
    }
  };

  const SlotOfWeekSkeleton = ({}) => {
    return (
      <View
        style={{
          alignItems: "center",
          width: "100%",
          flexDirection: "row",
          flex: 1,
        }}
      >
        <View
          style={{
            width: "50%",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PulseSkeleton width={120} height={12} />
        </View>
        <View
          style={{
            width: "50%",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PulseSkeleton width={120} height={28} />
        </View>
      </View>
    );
  };

  const SlotOfWeek = observer(({ slotExercise, week }) => {
    const useCTAColors =
      week === 1 &&
      (protocolTitle === "primarySquat-string" ||
        protocolTitle === "primaryBenchPress-string" ||
        protocolTitle === "primaryDeadlift-string");

    const [showingExerciseWithDataWarning, setShowingExerciseWithDataWarning] =
      useState(false);

    const openPrimarySearchRef = useRef(null);

    // Define selectSlot at SlotOfWeek level so both SlotText and SlotTextNew can use it
    const selectSlot = (slotExercise, isSecondaryProtocol) => {
      if (slotExercise) {
        if (isSecondaryProtocol) {
          block.setProperty(
            "selectedSlotId",
            slotExercise.getSecondProtocol.id.toLowerCase(),
          );
        } else {
          block.setProperty("selectedSlotId", slotExercise.id.toLowerCase());
        }
      } else {
        block.setProperty("selectedSlotId", null);
        block.setProperty("isProtocolAttributesShowing", false);
        block.setProperty("selectedProtocolSlot", protocolTitle);
        block.setProperty("selectedProtocolWeek", week);
      }
      if (calendar && block.client) {
        block.client.setProperty("focusedBlock", block);
      }
      block.setProperty("showingInfoView", true);
    };

    //Old method. Keep for reference. Delete in two weeks.
    const SlotText = observer(({}) => {
      const [isHovered, setIsHovered] = useState(false);
      const [isPlusHovered, setIsPlusHovered] = useState(false);

      return (
        <View
          style={{
            alignItems: "center",
            flex: 1,
          }}
          onMouseEnter={() => {
            if (!isMobile) {
              setIsHovered(true);
            }
          }}
          onMouseLeave={() => setIsHovered(false)} // Hide tooltip when not hovering
        >
          {slotExercise ? (
            <>
              <Text
                numberOfLines={2}
                onPress={() => selectSlot(slotExercise)}
                style={{
                  width: "100%",
                  color: "black",
                  textAlign: "center",
                  fontSize: screenSize === "small" ? 11 : 11,
                  padding: 2,
                }}
              >
                {slotExercise.getName(t, i18n.language)}
              </Text>
              {slotExercise.getSecondProtocol && (
                <>
                  <Text
                    style={{
                      width: "100%",
                      color: "black",
                      textAlign: "center",
                      fontSize: screenSize === "small" ? 11 : 11,
                      padding: 2,
                    }}
                  >
                    +
                  </Text>
                  <Text
                    numberOfLines={2}
                    onPress={() => selectSlot(slotExercise, true)}
                    style={{
                      width: "100%",
                      color: "black",
                      textAlign: "center",
                      fontSize: screenSize === "small" ? 11 : 11,
                      padding: 2,
                    }}
                  >
                    {slotExercise.getSecondProtocol.getName(t, i18n.language)}
                  </Text>
                </>
              )}

              {(isHovered || isMobile) && !slotExercise.getSecondProtocol && (
                <TouchableOpacity
                  onPress={() => {
                    if (calendar && block.client) {
                      block.client.setProperty("focusedBlock", block);
                    }
                    slotExercise.createSecondProtocol();
                    block.setProperty("isProtocolAttributesShowing", false);
                    setIsHovered(false);
                  }}
                  style={{
                    width: "50%",
                    alignItems: "center",
                    backgroundColor: Colors.lightGrayColor,
                    borderRadius: 10,
                    marginTop: 5,
                    outlineStyle: "none",
                  }}
                >
                  <AntDesign name="plus" size={8} color={Colors.blackColor} />
                </TouchableOpacity>
              )}
            </>
          ) : (
            <TouchableOpacity
              onPress={() => selectSlot(null)}
              style={{
                width: "100%",
                maxWidth: 128,
                opacity: isPlusHovered ? 0.5 : 1,
                outlineStyle: "none",
              }}
              onMouseEnter={() => !isMobile && setIsPlusHovered(true)}
              onMouseLeave={() => setIsPlusHovered(false)}
            >
              <View
                style={{
                  width: "100%",
                  alignItems: "center",
                  backgroundColor: useCTAColors
                    ? Colors.bubbleColor
                    : Colors.lightGrayColor,
                  borderRadius: 10,
                }}
              >
                <AntDesign
                  name="plus"
                  size={10}
                  color={useCTAColors ? Colors.whiteColor : Colors.blackColor}
                />
              </View>
            </TouchableOpacity>
          )}
        </View>
      );
    });

    const ProtocolText = observer(({}) => {
      const [showingProtocolDetectionAlert, setShowingProtocolDetectionAlert] =
        useState(false);

      const ProtocolTextBox = observer(({ slotExercise }) => {
        const [isHovered, setIsHovered] = useState(false);
        const [showingEdit, setShowingEdit] = useState(false);
        const [shouldFocusAtEnd, setShouldFocusAtEnd] = useState(false);
        const textInputRef = useRef(null);

        const isSecondary = slotExercise?.isSecondaryProtocol ? 1 : 0;
        const protocolTextKey = `${protocolTitle}:${week}:${isSecondary}`;

        // Initialize edit mode based on whether protocol text is empty.
        useEffect(() => {
          setShowingEdit(slotExercise.protocolText === "");
        }, []);

        // Keep edit-mode ref in sync so we can snapshot it when focus is requested.
        useEffect(() => {
          const modes = protocolTextEditModesRef.current;
          showingEdit
            ? modes.add(protocolTextKey)
            : modes.delete(protocolTextKey);
          return () => modes.delete(protocolTextKey);
        }, [showingEdit, protocolTextKey]);

        // Perform focus when shouldFocusAtEnd is set.
        useEffect(() => {
          if (showingEdit && shouldFocusAtEnd && textInputRef.current) {
            setTimeout(() => {
              textInputRef.current?.focus();
              setShouldFocusAtEnd(false);
            }, 100);
          }
        }, [showingEdit, shouldFocusAtEnd]);

        // Handle pending focus request after exercise selection.
        useEffect(() => {
          const request =
            protocolTextFocusRequestsRef.current.get(protocolTextKey);
          if (!request) return;

          // Consume request.
          protocolTextFocusRequestsRef.current.delete(protocolTextKey);

          // Focus if already editing, or enter edit+focus if empty/was editing.
          if (
            showingEdit ||
            slotExercise?.protocolText === "" ||
            request.wasEditing
          ) {
            setShowingEdit(true);
            setShouldFocusAtEnd(true);
          }
        }, [
          focusRequestVersion,
          protocolTextKey,
          showingEdit,
          slotExercise?.protocolText,
        ]);

        const changeProtocol = (text, slotExercise) => {
          // Check if any of the series have values in actual
          const actualWithData = hasSeriesWithActualData(slotExercise);

          if (
            actualWithData &&
            !slotExercise.ignoreActualWithDataWarning &&
            slotExercise.workout
          ) {
            slotExercise.setProperty("showActualWithDataWarning", true);
            return;
          }
          slotExercise.setProperty("protocolText", text);

          runInAction(() => {
            updateExerciseWithProtocol(slotExercise, text, Serie);
          });
        };

        const addSuggestion = (suggestion) => {
          const newText = slotExercise.protocolText + suggestion;
          slotExercise.setProperty("protocolText", newText);
          runInAction(() => {
            updateExerciseWithProtocol(slotExercise, newText, Serie);
          });
        };
        // Compute error status only when the protocol text actually changes
        const protocolTextError = useMemo(
          () => hasProtocolTextError(slotExercise.protocolText),
          [slotExercise.protocolText],
        );
        const suggestion = getProtocolSuggestion(slotExercise.protocolText);

        const handleKeyPress = (e) => {
          const handled = handleProtocolKeyPress({
            event: e.nativeEvent,
            currentText: slotExercise.protocolText,
            onAcceptSuggestion: (newText) => {
              // Check if any of the series have values in actual
              const actualWithData = hasSeriesWithActualData(slotExercise);

              if (actualWithData && !slotExercise.ignoreActualWithDataWarning) {
                slotExercise.setProperty("showActualWithDataWarning", true);
                return;
              }

              addSuggestion(suggestion);

              if (textInputRef.current) {
                textInputRef.current.focus();
              }
            },
            onExitEdit: () => setShowingEdit(false),
            preventDefault: () => e.preventDefault(),
          });

          if (!handled && slotExercise.protocolText !== "") {
            // If key wasn't handled but we have text, exit on Enter/Tab
            if (e.nativeEvent.key === "Enter" || e.nativeEvent.key === "Tab") {
              setShowingEdit(false);
              e.preventDefault();
            }
          }
        };

        return (
          <View
            style={{
              width: "100%",
              marginTop: slotExercise.isSecondaryProtocol ? 10 : 0,
            }}
          >
            <View
              style={{
                position: "relative",
                width: "100%",
                minHeight: 32,
              }}
              onMouseEnter={() => !isMobile && setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              {/* View layer (always mounted) */}
              <View
                pointerEvents={showingEdit ? "none" : "auto"}
                style={{
                  position: "relative",
                  width: "100%",
                  padding: showingEdit ? 0 : 2,
                  minHeight: showingEdit ? 0 : 32,
                  justifyContent: "center",
                  opacity: showingEdit ? 0 : 1,
                  height: showingEdit ? 0 : undefined,
                  // overflow: "hidden",
                }}
              >
                <Text
                  numberOfLines={3}
                  onPress={() => {
                    setShowingEdit(true);
                    setShouldFocusAtEnd(true);
                  }}
                  style={{
                    width: "100%",
                    color: "gray",
                    textAlign: "center",
                    fontSize: screenSize === "small" ? 10 : 10,
                    fontWeight: isHovered ? "600" : "normal",
                    padding: 2,
                  }}
                >
                  {slotExercise.protocolText}
                </Text>

                {protocolTextError && (
                  <AntDesign
                    name="exclamationcircleo"
                    size={14}
                    color="red"
                    style={{
                      position: "absolute",
                      top: -8,
                      right: -8,
                      padding: 2,
                      opacity: 0.7,
                    }}
                    onPress={() => {
                      const coach = block?.client?.coach ?? block.coach;
                      coach.setProperty("showingProtocolsGuide", false);
                      setTimeout(() => {
                        coach.setProperty("showingProtocolsGuide", true);
                      }, 0);
                    }}
                  />
                )}
              </View>

              {/* Edit layer (always mounted) */}
              <View
                style={{
                  position: "relative",
                  width: "100%",
                  borderWidth: 0.5,
                  borderColor: isHovered
                    ? "rgba(0,0,0,0.5)"
                    : Colors.borderColor,
                  borderRadius: 8,
                  minHeight: 32,
                  padding: 4,
                  display: showingEdit ? "flex" : "none",
                }}
                onMouseEnter={() => !isMobile && setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                {/* Typing animation overlay - only visible when empty and week 1 */}
                {showingEdit &&
                  slotExercise.protocolText === "" &&
                  useCTAColors &&
                  !slotExercise.isSecondaryProtocol && (
                    <View
                      style={{
                        position: "absolute",
                        top: 4,
                        left: 4,
                        right: 4,
                        bottom: 4,
                        justifyContent: "flex-start",
                        alignItems: "flex-start",
                        pointerEvents: "none",
                        zIndex: 0,
                      }}
                    >
                      <TypingAnimation
                        texts={[
                          "3x4 @5",
                          "175-180-185 x4",
                          "Ramp 3x4 @7 + Single @7",
                          "x4 @7 + 3x4 @5",
                          "x4 @4-5-6",
                          "3x5x200 + Single @8",
                        ]}
                        delay={80}
                        startDelay={300}
                        pauseBetween={2000}
                      />
                    </View>
                  )}

                {/* static display (with suggestion) behind the input */}
                <Text
                  style={{
                    fontSize: 11,
                    color: "transparent",
                    flexWrap: "wrap",
                    marginBottom: 2,
                  }}
                >
                  {slotExercise.protocolText}
                  {suggestion && (
                    <Text style={{ color: "grey" }}>{suggestion}</Text>
                  )}
                </Text>

                <TextInput
                  ref={textInputRef}
                  editable={showingEdit}
                  style={[
                    StyleSheet.absoluteFillObject,
                    {
                      top: 4,
                      left: 4,
                      right: 4,
                      bottom: 4,
                      color: "black",
                      fontSize: 11,
                      textAlignVertical: "top",
                      outline: "none",
                      backgroundColor: "transparent",
                      zIndex: 1,
                    },
                  ]}
                  value={slotExercise.protocolText}
                  selection={
                    showingEdit && shouldFocusAtEnd
                      ? {
                          start: slotExercise.protocolText.length,
                          end: slotExercise.protocolText.length,
                        }
                      : undefined
                  }
                  onChangeText={(text) => changeProtocol(text, slotExercise)}
                  autoCorrect={false}
                  autoCapitalize="none"
                  placeholderTextColor={Colors.grayColor}
                  multiline
                  onKeyPress={handleKeyPress}
                  blurOnSubmit={false}
                  maxLength={60}
                  onBlur={() => {
                    if (slotExercise.protocolText !== "") {
                      setShowingEdit(false);
                    }
                  }}
                  scrollEnabled={false}
                />
              </View>
            </View>
          </View>
        );
      });

      const EmptyTextBox = () => {
        const [isHovered, setIsHovered] = useState(false);

        return (
          <TouchableOpacity
            onPress={() => {
              if (openPrimarySearchRef.current) {
                openPrimarySearchRef.current(false);
              }
            }}
            style={{
              width: "100%",
              position: "relative",
              borderWidth: 0.5,
              borderColor: isHovered
                ? "rgba(0, 0, 0, 0.5)"
                : Colors.borderColor,
              borderRadius: 8,
              minHeight: 32,
              outlineStyle: "none",
            }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          />
        );
      };

      return (
        <View
          style={{
            alignItems: "center",
            flex: 1,
          }}
        >
          {slotExercise ? (
            <ProtocolTextBox slotExercise={slotExercise} />
          ) : (
            <EmptyTextBox />
          )}

          {slotExercise && slotExercise.getSecondProtocol && (
            <ProtocolTextBox slotExercise={slotExercise.getSecondProtocol} />
          )}

          {showingProtocolDetectionAlert && (
            <CustomAlert
              isVisible={showingProtocolDetectionAlert}
              onClose={() => setShowingProtocolDetectionAlert(false)}
              title={"protocolDetectionTitle-string"}
              text={"protocolDetectionText-string"}
            />
          )}
          {slotExercise?.showActualWithDataWarning && (
            <CustomAlert
              isVisible={slotExercise?.showActualWithDataWarning}
              onClose={() =>
                slotExercise?.setProperty("showActualWithDataWarning", false)
              }
              title={"actualWithDataProtocolWarningTitle-string"}
              text={"actualWithDataProtocolWarningText-string"}
              action={() => {
                slotExercise?.setProperty("ignoreActualWithDataWarning", true);
                slotExercise?.setProperty("showActualWithDataWarning", false);
              }}
              specialCTA={"modify-string"}
            />
          )}
        </View>
      );
    });

    const checkCopyProtocolWarning = () => {
      if (!slotExercise)
        console.error(
          "No slotExercise has been passed as parameter of checkCopyProtocolWarning",
        );

      const useWarning = block.checkIfSeriesWithData(
        slotExercise.getWeek + 1,
        slotExercise.protocolSlot,
      );
      if (useWarning) {
        setShowingExerciseWithDataWarning(true);
      } else {
        block.copyProtocol(slotExercise);
      }
    };

    return (
      <View
        style={{
          alignItems: "center",
          flexDirection: "row",
          minWidth: 264,
          minHeight: 32,
        }}
        onMouseEnter={() =>
          !isMobile &&
          slotExercise &&
          slotExercise.setProperty("isProtocolHovered", true)
        }
        onMouseLeave={() =>
          slotExercise && slotExercise.setProperty("isProtocolHovered", false)
        }
      >
        <View
          style={[
            GlobalStyles.HStack,
            {
              width: "100%",
              gap: 16,
            },
          ]}
        >
          <SlotTextNew
            slotExercise={slotExercise}
            selectSlot={selectSlot}
            useCTAColors={useCTAColors}
            calendar={calendar}
            block={block}
            protocolTitle={protocolTitle}
            week={week}
            onRegisterOpenSearch={(fn) => {
              openPrimarySearchRef.current = fn;
            }}
            onExerciseSetFinished={({ isSecondProtocol } = {}) => {
              const isSecondary = isSecondProtocol ? 1 : 0;
              const key = `${protocolTitle}:${week}:${isSecondary}`;
              const wasEditing = protocolTextEditModesRef.current.has(key);
              protocolTextFocusRequestsRef.current.set(key, { wasEditing });
              setFocusRequestVersion((v) => v + 1);
            }}
          />
          <ProtocolText />
        </View>
        <Observer>
          {() => (
            <>
              {slotExercise && week < block.totalWeeks && (
                <Tooltip
                  content="copySlotAndProtocol-string"
                  placement="top"
                  delayedOpen={true}
                >
                  <TouchableOpacity
                    onPress={checkCopyProtocolWarning}
                    style={{
                      backgroundColor: "rgba(0, 0, 0, 0.05)",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 8,
                      alignSelf: "center",
                      opacity:
                        slotExercise.isProtocolHovered || isMobile ? 1 : 0,
                      marginLeft: 8,
                      width: 24,
                      height: 32,
                      outlineStyle: "none",
                    }}
                  >
                    <Ionicons
                      name="arrow-forward-circle-outline"
                      size={20}
                      color={Colors.bubbleColor}
                    />
                  </TouchableOpacity>
                </Tooltip>
              )}
            </>
          )}
        </Observer>
        {showingExerciseWithDataWarning && (
          <CustomAlert
            isVisible={showingExerciseWithDataWarning}
            onClose={() => setShowingExerciseWithDataWarning(false)}
            title={"actualWithDataCopyProtocolWarningTitle-string"}
            text={t("actualWithDataCopyProtocolWarningText-string", {
              week: slotExercise?.getWeek + 1 ?? 1,
            })}
            action={() => {
              block.copyProtocol(slotExercise);
            }}
            specialCTA={"replace-string"}
          />
        )}
      </View>
    );
  });

  const SerieRowViewRework = ({ serie, idx, blockDashboardShowingActual }) => {
    const PropertyCases = {
      weight: "weight",
      reps: "reps",
      rpe: "rpe",
      targetWeight: "targetWeight",
      targetReps: "targetReps",
      targetRpe: "targetRpe",
    };

    const [refresh, setRefresh] = useState(false);
    useEffect(() => {
      const dispose = reaction(
        () => ({
          updateSets: serie.exercise.updateSets,
          updateSet: serie.updateSet,
        }),
        () => {
          setRefresh((prev) => !prev);
        },
      );

      return () => dispose();
    }, [serie.exercise]);

    const TextBox = ({ property, propertyCase }) => {
      const isTargetCase = [
        PropertyCases.targetWeight,
        PropertyCases.targetReps,
        PropertyCases.targetRpe,
      ].includes(propertyCase);

      const initialValue =
        typeof property === "string"
          ? property
          : property !== 0 && property != null
            ? formatNumber1Dec(property)
            : "";

      const [propertyString, setPropertyString] = useState(initialValue);
      const [isHovered, setIsHovered] = useState(false);
      const [isEditing, setIsEditing] = useState(false);
      const inputRef = useRef(null);

      useEffect(() => {
        setPropertyString(
          typeof property === "string"
            ? property
            : property !== 0 && property != null
              ? formatNumber1Dec(property)
              : "",
        );
      }, [refresh]);

      // useEffect(() => {
      //   if (isEditing && inputRef.current) {
      //     inputRef.current.focus();
      //   }
      // }, [isEditing]);

      const handleChange = (text) => {
        const formattedText = text.replace(",", ".");

        if (isTargetCase) {
          setPropertyString(formattedText);
          serie.setProperty(propertyCase, formattedText);
          return;
        }

        const aux = Number(formattedText);

        if (!isNaN(aux)) {
          setPropertyString(formattedText);

          let numAux;
          if (formattedText === "") {
            numAux = Number(formattedText);
          } else {
            numAux = Number(formattedText) + 0.0000001;
          }

          if (propertyCase === PropertyCases.weight) {
            serie.setProperty("weight", numAux);
          } else if (propertyCase === PropertyCases.reps) {
            serie.setProperty("reps", Math.round(numAux));
          } else if (propertyCase === PropertyCases.rpe) {
            serie.setProperty("rpe", numAux);
          }
        }
      };

      // const handleBlur = () => {
      //   setIsEditing(false);
      // };

      // const handleKeyPress = (e) => {
      //   if (e.nativeEvent.key === "Enter") {
      //     setIsEditing(false);
      //   }
      // };

      const getBackgroundColor = (serie) => {
        if (!serie) return "transparent";

        const target = serie.getTargetRpeReference;
        const actual = serie.rpe;

        if (typeof target === "number" && target > 0.02 && actual > 0.02) {
          // round both to one decimal (to match your formatNumber logic)
          const a = parseFloat(formatNumber1Dec(actual));
          const t = parseFloat(formatNumber1Dec(target));

          if (Math.abs(a - t) >= 1) {
            return a > t
              ? "rgba(240, 68, 56, 0.15)" // too heavy
              : "rgba(93, 196, 96, 0.15)"; // too light
          }
        }

        return "transparent";
      };

      const isActualEmpty =
        !isTargetCase &&
        (propertyString == null || propertyString.trim() === "");

      const effectiveBorderColor = isHovered
        ? isActualEmpty
          ? "rgb(200,200,200)"
          : "gray"
        : isActualEmpty
          ? Colors.borderColor
          : Colors.grayColor;

      // For "Actual" fields, render Text by default, swap to TextInput on tap/focus
      // if (!isTargetCase && !isEditing) {
      //   return (
      //     <View
      //       style={{
      //         borderColor: isHovered ? "gray" : Colors.grayColor,
      //         height: screenSize === "small" ? 24 : 24,
      //         borderRadius: screenSize === "small" ? 10 : 10,
      //         borderWidth: isHovered ? 1.2 : 1,
      //         justifyContent: "center",
      //         alignItems: "center",
      //         width: 48,
      //         backgroundColor:
      //           propertyCase === PropertyCases.rpe && getBackgroundColor(serie),
      //       }}
      //       onMouseEnter={() => !isMobile && setIsHovered(true)}
      //       onMouseLeave={() => setIsHovered(false)}
      //     >
      //       <TouchableOpacity
      //         style={{ width: "100%", height: 24, justifyContent: "center" }}
      //         onPress={() => setIsEditing(true)}
      //         activeOpacity={0.7}
      //       >
      //         <Text
      //           style={{
      //             width: "100%",
      //             textAlign: "center",
      //             color: "black",
      //             fontSize: screenSize === "small" ? 11 : 11,
      //           }}
      //           numberOfLines={1}
      //         >
      //           {propertyString}
      //         </Text>
      //       </TouchableOpacity>
      //     </View>
      //   );
      // }

      // Target fields, or "Actual" fields in edit mode
      return (
        <View
          style={{
            borderColor: effectiveBorderColor,
            height: screenSize === "small" ? 24 : 24,
            borderRadius: screenSize === "small" ? 10 : 10,
            borderWidth: isHovered ? 1.2 : 1,
            justifyContent: "center",
            alignItems: "center",
            width: 48,
            backgroundColor:
              propertyCase === PropertyCases.rpe && getBackgroundColor(serie),
          }}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <TextInput
            ref={inputRef}
            value={propertyString}
            onChangeText={handleChange}
            inputMode={isTargetCase ? "text" : "numeric"}
            style={{
              width: "100%",
              height: 24,
              borderRadius: screenSize === "small" ? 10 : 10,
              textAlign: "center",
              color: "black",
              fontSize: screenSize === "small" ? 11 : 11,
            }}
            maxLength={10}
            // onBlur={handleBlur}
            // onKeyPress={handleKeyPress}
          />
        </View>
      );
    };

    const copyTargetToActual = () => {
      serie.copyTargetToActual();
      setRefresh(!refresh);
    };

    const duplicateSerieBelow = () => {
      serie.exercise.duplicateSerieBelow(serie);
    };

    const Circles = () => {
      const [circlesMenu, setCirclesMenu] = useState(false);
      const showCirclesMenu = () => setCirclesMenu(true);

      const handleMenuItemPress = (relevance) => {
        serie.setProperty("relevance", relevance);
        setCirclesMenu(false);
      };

      const [isHovered, setIsHovered] = useState(false);

      const MenuItemView = ({ option }) => {
        const [isHovered, setIsHovered] = useState(false);

        return option === 1 ? (
          <MenuItem
            onPress={() => handleMenuItemPress(1)}
            style={{ minWidth: 160 }}
            textStyle={{
              fontSize: 14,
              color: Colors.blackColor,
              fontWeight: isHovered ? "600" : "normal",
            }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <View
              style={[
                GlobalStyles.circle,
                { backgroundColor: "red", borderWidth: 0.2 },
              ]}
            />

            <Text style={{ marginLeft: 6 }}>{t("highPriority-string")}</Text>
          </MenuItem>
        ) : option === 2 ? (
          <MenuItem
            onPress={() => handleMenuItemPress(2)}
            style={{ minWidth: 160 }}
            textStyle={{
              fontSize: 14,
              color: Colors.blackColor,
              fontWeight: isHovered ? "600" : "normal",
            }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <View
              style={[
                GlobalStyles.circle,
                { backgroundColor: "yellow", borderWidth: 0.2 },
              ]}
            />

            <Text style={{ marginLeft: 6 }}>{t("mediumPriority-string")}</Text>
          </MenuItem>
        ) : (
          <MenuItem
            onPress={() => handleMenuItemPress(3)}
            style={{ minWidth: 160 }}
            textStyle={{
              fontSize: 14,
              color: Colors.blackColor,
              fontWeight: isHovered ? "600" : "normal",
            }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <View
              style={[
                GlobalStyles.circle,
                { backgroundColor: "green", borderWidth: 0.2 },
              ]}
            />
            <Text style={{ marginLeft: 6 }}>{t("lowPriority-string")}</Text>
          </MenuItem>
        );
      };

      return (
        <Menu
          visible={circlesMenu}
          anchor={
            <Tooltip
              content="setPriority-string"
              placement="top"
              delayedOpen={true}
              disabled={circlesMenu}
            >
              <TouchableOpacity
                style={{ opacity: isHovered ? 0.5 : 1, outlineStyle: "none" }}
                onPress={showCirclesMenu}
                onMouseEnter={() => !isMobile && setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <View
                  style={[
                    GlobalStyles.circle,
                    {
                      backgroundColor:
                        serie.relevance === 1
                          ? "red"
                          : serie.relevance === 2
                            ? "yellow"
                            : "green",
                      borderWidth: 0.2,
                    },
                  ]}
                />
              </TouchableOpacity>
            </Tooltip>
          }
          style={GlobalStyles.menu}
          onRequestClose={() => setCirclesMenu(false)}
        >
          <MenuItemView option={1} />
          <MenuItemView option={2} />
          <MenuItemView option={3} />
        </Menu>
      );
    };

    const VideoView = observer(() => {
      const [circlesMenu, setCirclesMenu] = useState(false);

      async function handleVideoPress() {
        if (serie.hasVideo) {
          const coach = block?.client?.coach;
          if (coach && !coach.isOffline) {
            const mediaManager = MediaManager.getInstance();
            mediaManager.showMediaPlayer(undefined, serie);
          }
        } else {
          if (serie.requireVideo) {
            serie.setProperty("requireVideo", false);
          } else {
            serie.setProperty("requireVideo", true);
          }
        }
      }

      const handleMenuItemPress = (relevance) => {
        serie.setProperty("relevance", relevance);
        setCirclesMenu(false);
      };

      const [isHovered, setIsHovered] = useState(false);

      const MenuItemView = ({ option }) => {
        const [isHovered, setIsHovered] = useState(false);

        return option === 1 ? (
          <MenuItem
            onPress={() => handleMenuItemPress(1)}
            style={{ minWidth: 160 }}
            textStyle={{
              fontSize: 14,
              color: Colors.blackColor,
              fontWeight: isHovered ? "600" : "normal",
            }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <View
              style={[
                GlobalStyles.circle,
                { backgroundColor: "red", borderWidth: 0.2 },
              ]}
            />

            <Text style={{ marginLeft: 6 }}>{t("highPriority-string")}</Text>
          </MenuItem>
        ) : option === 2 ? (
          <MenuItem
            onPress={() => handleMenuItemPress(2)}
            style={{ minWidth: 160 }}
            textStyle={{
              fontSize: 14,
              color: Colors.blackColor,
              fontWeight: isHovered ? "600" : "normal",
            }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <View
              style={[
                GlobalStyles.circle,
                { backgroundColor: "yellow", borderWidth: 0.2 },
              ]}
            />

            <Text style={{ marginLeft: 6 }}>{t("mediumPriority-string")}</Text>
          </MenuItem>
        ) : (
          <MenuItem
            onPress={() => handleMenuItemPress(3)}
            style={{ minWidth: 160 }}
            textStyle={{
              fontSize: 14,
              color: Colors.blackColor,
              fontWeight: isHovered ? "600" : "normal",
            }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <View
              style={[
                GlobalStyles.circle,
                { backgroundColor: "green", borderWidth: 0.2 },
              ]}
            />
            <Text style={{ marginLeft: 6 }}>{t("lowPriority-string")}</Text>
          </MenuItem>
        );
      };

      return (
        <Menu
          visible={circlesMenu}
          anchor={
            <Tooltip
              content={
                serie.hasVideo
                  ? ""
                  : serie.requireVideo
                    ? "dontRequestVideo-string"
                    : "requestVideo-string"
              }
              placement="top"
              delayedOpen={true}
              disabled={serie.hasVideo}
            >
              <TouchableOpacity
                style={{ opacity: isHovered ? 0.5 : 1, outlineStyle: "none" }}
                onPress={handleVideoPress}
                onMouseEnter={() => !isMobile && setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <Ionicons
                  name={
                    serie.hasVideo
                      ? "videocam"
                      : serie.requireVideo
                        ? "videocam-outline"
                        : "videocam-off-outline"
                  }
                  size={screenSize === "large" ? 20 : 20}
                  color={
                    serie.hasVideo || serie.requireVideo
                      ? "black"
                      : "rgba(0, 0, 0, 0.2)"
                  }
                  style={{
                    marginLeft: 8,
                  }}
                />
              </TouchableOpacity>
            </Tooltip>
          }
          style={GlobalStyles.menu}
          onRequestClose={() => setCirclesMenu(false)}
        >
          <MenuItemView option={1} />
          <MenuItemView option={2} />
          <MenuItemView option={3} />
        </Menu>
      );
    });

    const CopyTargetButton = () => {
      const [isHovered, setIsHovered] = useState(false);

      return (
        <Tooltip
          content="copyTargetToActual-string"
          placement="top"
          delayedOpen={true}
        >
          <TouchableOpacity
            onPress={copyTargetToActual}
            style={{ width: 40, alignItems: "center", outlineStyle: "none" }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <Ionicons
              name={
                isHovered
                  ? "arrow-forward-circle"
                  : "arrow-forward-circle-outline"
              }
              size={screenSize === "large" ? 24 : 24}
              color={Colors.bubbleColor}
            />
          </TouchableOpacity>
        </Tooltip>
      );
    };

    const DuplicateSerieBelowButton = () => {
      const [isHovered, setIsHovered] = useState(false);

      return (
        <Tooltip
          content="copyToNextSet-string"
          placement="top"
          delayedOpen={true}
        >
          <TouchableOpacity
            onPress={duplicateSerieBelow}
            style={{ opacity: isHovered ? 0.5 : 1, outlineStyle: "none" }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <MaterialCommunityIcons
              name="arrow-down-right"
              size={screenSize === "large" ? 20 : 20}
              color={Colors.bubbleColor}
              style={{
                marginRight: 4,
              }}
            />
          </TouchableOpacity>
        </Tooltip>
      );
    };

    const DeleteSerieButton = () => {
      const [isHovered, setIsHovered] = useState(false);

      return (
        <Tooltip content="deleteSet-string" placement="top" delayedOpen={true}>
          <TouchableOpacity
            onPress={() => serie.exercise.deleteSerie(serie)}
            style={{ opacity: isHovered ? 0.5 : 1, outlineStyle: "none" }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <Feather
              name="x"
              size={screenSize == "large" ? 20 : 20}
              color={Colors.bubbleColor}
            />
          </TouchableOpacity>
        </Tooltip>
      );
    };

    return (
      <View>
        <View
          style={{
            width: "100%",
            alignItems: "center",
            flexDirection: "row",
            justifyContent: "center",
            marginTop:
              idx !== 0 ? 4 : serie.exercise.isSecondaryProtocol ? 16 : 0,
            gap: showSetPriority ? 8 : blockDashboardShowingActual ? 10 : 13,
          }}
        >
          <View
            style={{
              alignItems: "center",
              flexDirection: "row",
              width: showSetPriority ? 40 : 20,
              justifyContent: showSetPriority ? "center" : "flex-end",
            }}
          >
            {showSetPriority && <Circles />}
            <VideoView />
          </View>
          <TextBox
            property={serie.targetWeight}
            propertyCase={PropertyCases.targetWeight}
          />
          <TextBox
            property={serie.targetReps}
            propertyCase={PropertyCases.targetReps}
          />
          <TextBox
            property={serie.targetRpe}
            propertyCase={PropertyCases.targetRpe}
          />
          {blockDashboardShowingActual && (
            <View
              style={{
                alignItems: "center",
                flexDirection: "row",
                justifyContent: "center",
                gap: showSetPriority ? 8 : 10,
              }}
            >
              <CopyTargetButton />
              <TextBox
                property={serie.weight}
                propertyCase={PropertyCases.weight}
              />
              <TextBox
                property={serie.reps}
                propertyCase={PropertyCases.reps}
              />
              <TextBox property={serie.rpe} propertyCase={PropertyCases.rpe} />
            </View>
          )}
          <View
            style={{
              justifyContent: "space-around",
              flexDirection: "row",
              alignItems: "center",
              width: 48,
            }}
          >
            <DuplicateSerieBelowButton />
            <DeleteSerieButton />
          </View>
        </View>
      </View>
    );
  };

  const SlotOfWeekForSeries = observer(({ slotExercise }) => {
    const SerieHeader = observer(() => {
      const [isHovered, setIsHovered] = useState(false);
      const [isPlusHovered, setIsPlusHovered] = useState(false);
      const [isLinkHovered, setIsLinkHovered] = useState(false);
      const [isLinkFloatingOpen, setIsLinkFloatingOpen] = useState(false);

      const Header = ({ gap = 8, width = 160 }) => {
        return (
          <View
            style={{
              width: width,
              alignItems: "center",
              flexDirection: "row",
              gap: gap,
            }}
          >
            <Text
              style={{
                width: 48,
                textAlign: "center",
                fontSize: screenSize === "small" ? 11.5 : 11.5,
                fontWeight: "600",
                color: Colors.grayColor,
              }}
              numberOfLines={1}
            >
              {t("weight-string")}
            </Text>
            <Text
              style={{
                width: 48,
                textAlign: "center",
                fontSize: screenSize === "small" ? 11.5 : 11.5,
                fontWeight: "600",
                color: Colors.grayColor,
              }}
              numberOfLines={1}
            >
              REPS
            </Text>
            <Text
              style={{
                width: 48,
                textAlign: "center",
                fontSize: screenSize === "small" ? 11.5 : 11.5,
                fontWeight: "600",
                color: Colors.grayColor,
              }}
              numberOfLines={1}
            >
              {slotExercise?.getRirOrRpe ? "RIR" : "RPE"}
            </Text>
          </View>
        );
      };

      const AddSerieButton = () => {
        const [addSerieMenu, setAddSerieMenu] = useState(false);

        const MenuItemView = ({ option }) => {
          const [isHovered, setIsHovered] = useState(false);

          const handleMenuItemPress = (firstExercise) => {
            if (firstExercise) {
              slotExercise.addSerie();
            } else {
              slotExercise.getSecondProtocol.addSerie();
            }

            setAddSerieMenu(false);
          };
          return option === 0 ? (
            <MenuItem
              onPress={() => handleMenuItemPress(true)}
              style={{ minWidth: 160 }}
              textStyle={{
                fontSize: 14,
                color: Colors.blackColor,
                fontWeight: isHovered ? "600" : "normal",
              }}
              onMouseEnter={() => !isMobile && setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <Text style={{}}>{t("firstExercise-string")}</Text>
            </MenuItem>
          ) : (
            option === 1 && (
              <MenuItem
                onPress={() => handleMenuItemPress(false)}
                style={{ minWidth: 160 }}
                textStyle={{
                  fontSize: 14,
                  color: Colors.blackColor,
                  fontWeight: isHovered ? "600" : "normal",
                }}
                onMouseEnter={() => !isMobile && setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <Text style={{}}>{t("secondExercise-string")}</Text>
              </MenuItem>
            )
          );
        };
        return !slotExercise.getSecondProtocol ? (
          <Tooltip content="addSet-string" placement="top" delayedOpen={true}>
            <TouchableOpacity
              style={{
                marginLeft: -1,
                opacity: isHovered || isMobile ? (isPlusHovered ? 0.6 : 1) : 0,
              }}
              onPress={() => {
                slotExercise?.addSerie();
              }}
              onMouseEnter={() => !isMobile && setIsPlusHovered(true)}
              onMouseLeave={() => setIsPlusHovered(false)}
            >
              <FontAwesome6 name="plus" size={13} color={Colors.grayColor} />
            </TouchableOpacity>
          </Tooltip>
        ) : (
          <Menu
            visible={addSerieMenu}
            anchor={
              <Tooltip
                content="addSet-string"
                placement="top"
                delayedOpen={true}
                disabled={addSerieMenu}
              >
                <TouchableOpacity
                  style={{
                    width: 16,
                    marginLeft: -1,
                    opacity:
                      isHovered || isMobile ? (isPlusHovered ? 0.6 : 1) : 0,
                  }}
                  onPress={() => setAddSerieMenu(true)}
                  // onMouseEnter={() => !isMobile && setIsPlusHovered(true)}
                  // onMouseLeave={() => setIsPlusHovered(false)}
                >
                  <FontAwesome6
                    name="plus"
                    size={13}
                    color={Colors.grayColor}
                  />
                </TouchableOpacity>
              </Tooltip>
            }
            style={GlobalStyles.menu}
            onRequestClose={() => setAddSerieMenu(false)}
          >
            <MenuItemView option={0} />
            <MenuItemView option={1} />
          </Menu>
        );
      };

      const selectedExerciseRef = useRef(slotExercise);

      const ExerciseLinkButton = () => {
        const [linkMenu, setLinkMenu] = useState(false);

        const MenuItemView = ({ option }) => {
          const [isHovered, setIsHovered] = useState(false);

          const handleMenuItemPress = (firstExercise) => {
            const targetExercise = firstExercise
              ? slotExercise
              : slotExercise.getSecondProtocol;

            selectedExerciseRef.current = targetExercise;
            setLinkMenu(false);
            setTimeout(() => {
              setIsLinkFloatingOpen(true);
            }, 50);
          };

          return option === 0 ? (
            <MenuItem
              onPress={() => handleMenuItemPress(true)}
              style={{ minWidth: 160 }}
              textStyle={{
                fontSize: 14,
                color: Colors.blackColor,
                fontWeight: isHovered ? "600" : "normal",
              }}
              onMouseEnter={() => !isMobile && setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <Text style={{}}>{t("firstExercise-string")}</Text>
            </MenuItem>
          ) : (
            option === 1 && (
              <MenuItem
                onPress={() => handleMenuItemPress(false)}
                style={{ minWidth: 160 }}
                textStyle={{
                  fontSize: 14,
                  color: Colors.blackColor,
                  fontWeight: isHovered ? "600" : "normal",
                }}
                onMouseEnter={() => !isMobile && setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <Text style={{}}>{t("secondExercise-string")}</Text>
              </MenuItem>
            )
          );
        };

        return !slotExercise.getSecondProtocol ? (
          <FloatingExerciseLink
            exercise={slotExercise}
            isOpen={isLinkFloatingOpen}
            onOpenChange={setIsLinkFloatingOpen}
            onClose={() => {
              setIsLinkHovered(false);
              setIsLinkFloatingOpen(false);
            }}
          >
            <Tooltip
              content="addVideoLink-string"
              placement="top"
              delayedOpen={true}
              disabled={isLinkFloatingOpen}
            >
              <View
                style={{
                  opacity:
                    isHovered || isMobile ? (isLinkHovered ? 0.6 : 1) : 0,
                }}
                onMouseEnter={() => !isMobile && setIsLinkHovered(true)}
                onMouseLeave={() => setIsLinkHovered(false)}
              >
                <Observer>
                  {() => (
                    <Feather
                      name="link"
                      size={13}
                      color={
                        slotExercise.link
                          ? Colors.bubbleColor
                          : Colors.grayColor
                      }
                    />
                  )}
                </Observer>
              </View>
            </Tooltip>
          </FloatingExerciseLink>
        ) : (
          <>
            <Menu
              visible={linkMenu}
              anchor={
                <Tooltip
                  content="addVideoLink-string"
                  placement="top"
                  delayedOpen={true}
                  disabled={linkMenu}
                >
                  <TouchableOpacity
                    style={{
                      marginLeft: -4,
                      opacity:
                        isHovered || isMobile ? (isLinkHovered ? 0.6 : 1) : 0,
                    }}
                    onPress={() => {
                      console.log("Link button pressed, showing menu");
                      setLinkMenu(true);
                    }}
                  >
                    <Observer>
                      {() => (
                        <Feather
                          name="link"
                          size={13}
                          color={
                            slotExercise.link ||
                            slotExercise.getSecondProtocol.link
                              ? Colors.bubbleColor
                              : Colors.grayColor
                          }
                        />
                      )}
                    </Observer>
                  </TouchableOpacity>
                </Tooltip>
              }
              style={GlobalStyles.menu}
              onRequestClose={() => setLinkMenu(false)}
            >
              <MenuItemView option={0} />
              <MenuItemView option={1} />
            </Menu>
            <FloatingExerciseLink
              exercise={selectedExerciseRef.current}
              isOpen={isLinkFloatingOpen}
              onOpenChange={setIsLinkFloatingOpen}
              onClose={() => {
                setIsLinkHovered(false);
                setIsLinkFloatingOpen(false);
              }}
            >
              <View style={{ display: "none" }} />
            </FloatingExerciseLink>
          </>
        );
      };

      return (
        <View
          style={{
            width: "100%",
            alignItems: "center",
            flexDirection: "row",
            justifyContent: showSetPriority ? "center" : "flex-start",
            gap: showSetPriority
              ? 8
              : slotExercise?.getBlockDashboardShowingActual
                ? 10
                : 13,
            marginTop: 16,
            marginBottom: 4,
          }}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => !isLinkFloatingOpen && setIsHovered(false)}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: showSetPriority ? "center" : "flex-start",
              width: showSetPriority ? 40 : 20,
              gap: 6,
            }}
          >
            <AddSerieButton />
            <ExerciseLinkButton />
          </View>

          <Header
            gap={
              showSetPriority
                ? 8
                : slotExercise?.getBlockDashboardShowingActual
                  ? 10
                  : 13
            }
            width={
              showSetPriority
                ? 160
                : slotExercise?.getBlockDashboardShowingActual
                  ? 164
                  : 170
            }
          />
          {slotExercise?.getBlockDashboardShowingActual && (
            <View
              style={{
                alignItems: "center",
                flexDirection: "row",
                justifyContent: showSetPriority ? "center" : "flex-start",
                gap: showSetPriority ? 8 : 10,
              }}
            >
              <View style={{ width: 40 }} />
              <Header
                gap={showSetPriority ? 8 : 10}
                width={showSetPriority ? 160 : 164}
              />
            </View>
          )}

          <View style={{ width: 48 }} />
        </View>
      );
    });

    const TextNotes = ({ exercise }) => {
      const [string, setString] = useState(
        exercise.notes ? exercise.notes : "",
      );
      const [refresh, setRefresh] = useState(false);
      const [showingEdit, setShowingEdit] = useState(false);
      const [shouldFocusAtEnd, setShouldFocusAtEnd] = useState(false);

      const [isHovered, setIsHovered] = useState(false);
      const textInputRef = useRef();

      useEffect(() => {
        setString(exercise.notes ? exercise.notes : "");
      }, [refresh]);

      useEffect(() => {
        const dispose = reaction(
          () => ({
            updateSets: exercise.updateSets,
          }),
          () => {
            setRefresh((prev) => !prev);
          },
        );

        return () => dispose();
      }, []);

      useEffect(() => {
        if (showingEdit && shouldFocusAtEnd && textInputRef.current) {
          setTimeout(() => {
            textInputRef.current?.focus();
            // Reset the flag after focusing
            setShouldFocusAtEnd(false);
          }, 100);
        }
      }, [showingEdit, shouldFocusAtEnd]);

      const handleChange = (text) => {
        setString(text);
        exercise.setProperty("notes", text);
      };

      return (
        <View
          style={[
            GlobalStyles.HStack,
            {
              flex: 1,
              flexShrink: 1,
              marginTop: 12,
              alignItems: "flex-start",
              justifyContent: "flex-start",
              alignSelf: "flex-start",
              width: columnWidth,
            },
          ]}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <View
            style={{
              position: "relative",
              width: "100%",
              minHeight: showingEdit ? 52 : 32,
              flex: 1,
            }}
          >
            {/* View layer (always mounted) */}
            <TouchableOpacity
              pointerEvents={showingEdit ? "none" : "auto"}
              style={{
                flex: 1,
                outlineStyle: "none",
                opacity: showingEdit ? 0 : 1,
              }}
              onPress={() => {
                setShowingEdit(true);
                setShouldFocusAtEnd(true);
              }}
            >
              <Text
                numberOfLines={3}
                style={{
                  alignSelf: "flex-start",
                  color: "gray",
                  fontSize: screenSize === "small" ? 10 : 10,
                  fontWeight: isHovered ? "600" : "normal",
                }}
              >
                {string != "" ? string : t("notes-string") + "..."}
              </Text>
            </TouchableOpacity>

            {/* Edit layer (always mounted) */}
            <View
              pointerEvents={showingEdit ? "auto" : "none"}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: "100%",
                borderWidth: 0.5,
                borderColor: isHovered ? "rgba(0,0,0,0.5)" : Colors.borderColor,
                borderRadius: 8,
                minHeight: 32,
                overflow: "hidden",
                opacity: showingEdit ? 1 : 0,
              }}
            >
              <TextInput
                ref={textInputRef}
                editable={showingEdit}
                value={string}
                onChangeText={handleChange}
                multiline
                numberOfLines={3}
                selection={
                  showingEdit && shouldFocusAtEnd
                    ? {
                        start: string.length,
                        end: string.length,
                      }
                    : undefined
                }
                style={{
                  fontSize: 11,
                  paddingVertical: 6,
                  paddingHorizontal: 8,
                  flex: 1,
                  outline: "none",
                }}
                onBlur={() => {
                  setShowingEdit(false);
                }}
              />
            </View>
          </View>
        </View>
      );
    };

    return (
      <View style={{ alignItems: "flex-start", minWidth: 264, minHeight: 32 }}>
        <SerieHeader />
        {slotExercise?.series?.map((serie, idx) => (
          <SerieRowViewRework
            key={serie.id}
            serie={serie}
            idx={idx}
            blockDashboardShowingActual={
              slotExercise?.getBlockDashboardShowingActual
            }
          />
        ))}
        {slotExercise?.series?.length > 0 && (
          <TextNotes exercise={slotExercise} />
        )}
        {slotExercise?.getSecondProtocol?.series?.map((serie, idx) => (
          <SerieRowViewRework
            key={serie.id}
            serie={serie}
            idx={idx}
            blockDashboardShowingActual={
              slotExercise?.getBlockDashboardShowingActual
            }
          />
        ))}
        {slotExercise?.getSecondProtocol?.series?.length > 0 && (
          <TextNotes exercise={slotExercise.getSecondProtocol} />
        )}
      </View>
    );
  });

  return (
    <View
      style={{
        marginTop:
          protocolTitle === ProtocolTitles.PRIMARY_SQUAT ||
          protocolTitle === ProtocolTitles.PRIMARY_BENCH_PRESS ||
          protocolTitle === ProtocolTitles.PRIMARY_DEADLIFT
            ? 0
            : 16,
        width: "100%",
        alignItems: "center",
      }}
    >
      <View
        style={{
          width: "100%",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <View
          style={{
            width: 180,
            textAlign: "center",
            paddingHorizontal: 16,
          }}
        >
          <ProtocolRowTitleToggle
            block={block}
            protocolTitle={protocolTitle}
            calendar={calendar}
          />
        </View>
        <Observer>
          {() => {
            const totalWeeks = block.totalWeeks || 0;
            const { start = 0, end = Math.max(0, totalWeeks - 1) } =
              visibleRange || {};
            const colWidth = columnWidth;
            const weeks = [];
            for (let w = start; w <= end; w++) {
              weeks.push(w + 1);
            }

            return (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  // borderWidth: 10,
                }}
              >
                {/* Left side: show one skeleton per offscreen week before the visible range */}
                {Array.from({ length: Math.max(0, start) }).map((_, i) => {
                  const w = i + 1; // week number for left skeletons
                  return (
                    <View
                      key={`left-skel-${w}`}
                      style={{
                        marginRight: w !== totalWeeks ? 40 : 16,
                        width: colWidth,
                      }}
                    >
                      <SlotOfWeekSkeleton />
                    </View>
                  );
                })}
                {weeks.map((week) => (
                  <View
                    style={{
                      marginRight: week !== totalWeeks ? 40 : 16,
                      width: colWidth,
                    }}
                    key={week}
                  >
                    {(() => {
                      const slot = block.getSlotOfWeek(protocolTitle, week);
                      return <SlotOfWeek slotExercise={slot} week={week} />;
                    })()}
                  </View>
                ))}
                {/* Right side: show one skeleton per offscreen week after the visible range */}
                {Array.from({
                  length: Math.max(0, totalWeeks - (end + 1)),
                }).map((_, i) => {
                  const w = end + 2 + i; // actual week index after visible range
                  return (
                    <View
                      key={`right-skel-${w}`}
                      style={{
                        marginRight: w !== totalWeeks ? 40 : 16,
                        width: colWidth,
                      }}
                    >
                      <SlotOfWeekSkeleton />
                    </View>
                  );
                })}
              </View>
            );
          }}
        </Observer>
      </View>

      <Observer>
        {() =>
          block.getProtocolSeriesShown(protocolTitle) && (
            <View
              style={{
                width: "100%",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <View
                style={{
                  width: 180,
                  textAlign: "center",
                  paddingHorizontal: 8,
                }}
              />
              <Observer>
                {() => {
                  const totalWeeks = block.totalWeeks || 0;
                  const { start = 0, end = Math.max(0, totalWeeks - 1) } =
                    visibleRange || {};
                  const colWidth = columnWidth;
                  const weeks = [];
                  for (let w = start; w <= end; w++) weeks.push(w + 1);
                  let leftSpacer = 0;
                  if (start > 0) {
                    if (start < totalWeeks - 1) {
                      leftSpacer = start * (colWidth + 40);
                    } else if (start === totalWeeks - 1) {
                      leftSpacer = (totalWeeks - 1) * (colWidth + 40);
                    }
                  }
                  let rightSpacer = 0;
                  if (end < totalWeeks - 1) {
                    rightSpacer =
                      (totalWeeks - (end + 2)) * (colWidth + 40) +
                      (colWidth + 16);
                  }
                  return (
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "flex-start",
                      }}
                    >
                      {leftSpacer > 0 && (
                        <View
                          style={{
                            width: leftSpacer,
                            height: 1,
                          }}
                        />
                      )}
                      {weeks.map((week) => {
                        const slot = block.getSlotOfWeek(protocolTitle, week);
                        return (
                          <View
                            style={{
                              minWidth: colWidth,
                              marginRight: week !== totalWeeks ? 40 : 16,
                              alignSelf: "flex-start",
                            }}
                            key={week}
                          >
                            {slot?.series?.length > 0 && (
                              <SlotOfWeekForSeries slotExercise={slot} />
                            )}
                          </View>
                        );
                      })}
                      {/* Always render right spacer for debug, even if width 0 */}
                      {rightSpacer > 0 && (
                        <View
                          style={{
                            width: rightSpacer,
                            height: 1,
                          }}
                        />
                      )}
                    </View>
                  );
                }}
              </Observer>
            </View>
          )
        }
      </Observer>
    </View>
  );
};

export default ProtocolRow;
